import { useParams } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { CouncilDashboard } from "@/components/CouncilDashboard"
import { SuperAdminStatsSection } from "@/components/dashboard/SuperAdminStatsSection"
import { AdvancedAnalyticsSection } from "@/components/dashboard/AdvancedAnalyticsSection"
import { UserAnalyticsSection } from "@/components/dashboard/UserAnalyticsSection"
import useGetAdminDashboard from "@/hooks/api/use-get-admin-dashboard"
import useGetLeakTrends from "@/hooks/api/use-get-leak-trends"
import type { AdminCouncilInfo, LeakTrendsDTO } from "@/gen/api"
import type { FC } from "react"
import useCurrentUser from "@/hooks/use-current-user"

interface LeakTrendData {
  month: string
  activeLeaks: number
  clearedLeaks: number
}

interface DevicePerformanceData {
  customer: string
  totalDevices: number
  activeDevices: number
  alertDevices: number
  offlineDevices: number
  efficiency: number
}

interface AnalyticsExportData {
  leakDetectionTrends: LeakTrendData[]
  endUserPortalDevices: DevicePerformanceData[]
}

const buildLeakTrendRows = (leakTrends?: LeakTrendsDTO): LeakTrendData[] => {
  const aggregatedTrends: Record<string, LeakTrendData> = {}
  const addTrend = (month: string, activeLeaks: number, clearedLeaks: number) => {
    if (!aggregatedTrends[month]) {
      aggregatedTrends[month] = {
        month,
        activeLeaks: 0,
        clearedLeaks: 0,
      }
    }
    aggregatedTrends[month].activeLeaks += activeLeaks
    aggregatedTrends[month].clearedLeaks += clearedLeaks
  }

  for (const trend of leakTrends?.activeLeaks ?? []) {
    addTrend(trend.period || "", trend.count ?? 0, 0)
  }
  for (const trend of leakTrends?.clearedLeaks ?? []) {
    addTrend(trend.period || "", 0, trend.count ?? 0)
  }

  return Object.values(aggregatedTrends).sort((a, b) => a.month.localeCompare(b.month))
}

export const Dashboard: FC = () => {
  const { councilId } = useParams()
  const { user } = useAuth()

  const hasCouncils = (user?.councils?.length ?? 0) > 0

  if (councilId || hasCouncils) {
    return <CouncilDashboard />
  }

  return <AdminDashboard />
}

export function AdminDashboard() {
  const { councilId } = useParams()
  const { user } = useAuth()
  const { hasRole } = useCurrentUser()
  const { data: adminDashboardData, isLoading, error } = useGetAdminDashboard()
  const { data: leakTrends, isLoading: isLeakTrendsLoading } = useGetLeakTrends()

  const exportData = (type: string, format: string) => {
    let data: unknown
    let content = ""
    let mimeType = ""
    let filename = ""

    // Get the appropriate data
    switch (type) {
      case "councils":
        data = adminDashboardData?.councilInfos || []
        break
      case "analytics": {
        // Focus on Leak Detection Trends and End User Portal Devices data
        const leakTrendsData = buildLeakTrendRows(leakTrends)
        const devicePerformanceData = []

        if (adminDashboardData?.councilInfos) {
          // Device performance data per customer
          devicePerformanceData.push(
            ...adminDashboardData.councilInfos.map((council) => ({
              customer: council.name || "Unknown",
              totalDevices: council.deviceCount || 0,
              activeDevices: council.activeCount || 0,
              alertDevices: council.alertDeviceCount || 0,
              offlineDevices: (council.deviceCount || 0) - (council.activeCount || 0),
              efficiency:
                council.deviceCount && council.activeCount
                  ? (council.activeCount / council.deviceCount) * 100
                  : 0,
            })),
          )
        }

        data = {
          leakDetectionTrends: leakTrendsData,
          endUserPortalDevices: devicePerformanceData,
        }
        break
      }
      case "user-analytics":
        // Focus on end user-related data only
        data = (adminDashboardData?.councilInfos || []).map((council) => ({
          customer: council.name || "Unknown",
          location: council.location || "N/A",
          userCount: council.userCount || 0,
          activeUsers: council.activeCount || 0, // Assuming activeCount represents active end users
          totalDevices: council.deviceCount || 0,
        }))
        break
      case "consumption":
        data = adminDashboardData?.councilInfos || []
        break
      default:
        data = adminDashboardData?.councilInfos || []
    }

    // Generate content based on format
    switch (format) {
      case "csv": {
        if (type === "analytics" && typeof data === "object" && data !== null) {
          // Special handling for analytics export - create separate files and zip them
          const analyticsData = data as AnalyticsExportData
          const leakTrends = analyticsData.leakDetectionTrends || []
          const deviceData = analyticsData.endUserPortalDevices || []

          // Import JSZip dynamically
          import("jszip")
            .then(({ default: JSZip }) => {
              const zip = new JSZip()

              // Create leak trends CSV
              if (leakTrends.length > 0) {
                let leakCsvContent = "Month,Active Leaks,Cleared Leaks\n"
                leakTrends.forEach((trend: LeakTrendData) => {
                  leakCsvContent += `${trend.month || ""},${trend.activeLeaks ?? 0},${trend.clearedLeaks ?? 0}\n`
                })
                zip.file("leak-analytics.csv", leakCsvContent)
              }

              // Create device performance CSV
              if (deviceData.length > 0) {
                let deviceCsvContent =
                  "Customer,Total Devices,Active Devices,Alert Devices,Offline Devices,Efficiency (%)\n"
                deviceData.forEach((device: DevicePerformanceData) => {
                  deviceCsvContent += `"${device.customer || ""}",${device.totalDevices || 0},${device.activeDevices || 0},${device.alertDevices || 0},${device.offlineDevices || 0},${device.efficiency ? device.efficiency.toFixed(1) : 0}\n`
                })
                zip.file("end-user-portal-devices.csv", deviceCsvContent)
              }

              // Generate and download zip file
              zip.generateAsync({ type: "blob" }).then((content) => {
                const url = URL.createObjectURL(content)
                const link = document.createElement("a")
                link.href = url
                link.download = "analytics-export.zip"
                link.click()
                URL.revokeObjectURL(url)
              })
            })
            .catch((error) => {
              console.error("Error creating zip file:", error)
              // Fallback to single CSV if JSZip fails
              let csvContent = ""

              if (leakTrends.length > 0) {
                csvContent += "Leak Analytics\n"
                csvContent += "Month,Active Leaks,Cleared Leaks\n"
                leakTrends.forEach((trend: LeakTrendData) => {
                  csvContent += `${trend.month || ""},${trend.activeLeaks ?? 0},${trend.clearedLeaks ?? 0}\n`
                })
                csvContent += "\n"
              }

              if (deviceData.length > 0) {
                csvContent += "End User Portal Devices\n"
                csvContent +=
                  "Customer,Total Devices,Active Devices,Alert Devices,Offline Devices,Efficiency (%)\n"
                deviceData.forEach((device: DevicePerformanceData) => {
                  csvContent += `"${device.customer || ""}",${device.totalDevices || 0},${device.activeDevices || 0},${device.alertDevices || 0},${device.offlineDevices || 0},${device.efficiency ? device.efficiency.toFixed(1) : 0}\n`
                })
              }

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              })
              const url = URL.createObjectURL(blob)
              const link = document.createElement("a")
              link.href = url
              link.download = "analytics-export.csv"
              link.click()
              URL.revokeObjectURL(url)
            })

          return // Early return since zip generation is async
        } else if (Array.isArray(data)) {
          const headers = Object.keys(data[0] || {}).join(",")
          const rows = data
            .map((item) => {
              return Object.values(item)
                .map((value) => {
                  if (Array.isArray(value)) {
                    // For arrays like leakTrends, serialize as JSON
                    return `"${JSON.stringify(value).replace(/"/g, '""')}"`
                  } else if (typeof value === "object" && value !== null) {
                    // For objects, serialize as JSON
                    return `"${JSON.stringify(value).replace(/"/g, '""')}"`
                  } else {
                    // For primitives, convert to string and escape quotes
                    return `"${String(value || "").replace(/"/g, '""')}"`
                  }
                })
                .join(",")
            })
            .join("\n")
          content = headers + "\n" + rows
        } else {
          content = "Name,Value\n" + `${type},${data}`
        }
        mimeType = "text/csv;charset=utf-8;"
        filename = `${type}-export.csv`
        break
      }

      case "pdf": {
        // Import jsPDF dynamically to generate proper PDF
        import("jspdf")
          .then(({ jsPDF }) => {
            const doc = new jsPDF()

            // Set up colors and styling
            const primaryColor: [number, number, number] = [41, 128, 185] // Blue
            const secondaryColor: [number, number, number] = [52, 73, 94] // Dark gray
            const accentColor: [number, number, number] = [231, 76, 60] // Red for alerts

            // Add header with background
            doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2])
            doc.rect(0, 0, 210, 40, "F")

            // Add title
            doc.setTextColor(255, 255, 255)
            doc.setFontSize(24)
            doc.setFont("helvetica", "bold")
            doc.text(`${type.charAt(0).toUpperCase() + type.slice(1)} Analytics Report`, 20, 25)

            // Add timestamp
            doc.setFontSize(10)
            doc.setFont("helvetica", "normal")
            doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, 35)

            if (type === "analytics" && typeof data === "object" && data !== null) {
              // Special handling for analytics PDF export
              const analyticsData = data as AnalyticsExportData
              const leakTrends = analyticsData.leakDetectionTrends || []
              const deviceData = analyticsData.endUserPortalDevices || []

              let yPosition = 60

              // Leak Analytics Section
              if (leakTrends.length > 0) {
                doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
                doc.setFontSize(18)
                doc.setFont("helvetica", "bold")
                doc.text("Leak Analytics", 20, yPosition)
                yPosition += 15

                doc.setFontSize(12)
                doc.setFont("helvetica", "normal")
                doc.setTextColor(0, 0, 0)

                // Add table headers
                doc.setFont("helvetica", "bold")
                doc.text("Month", 25, yPosition)
                doc.text("Active Leaks", 70, yPosition)
                doc.text("Cleared Leaks", 125, yPosition)
                yPosition += 8

                // Add horizontal line
                doc.setDrawColor(200, 200, 200)
                doc.line(20, yPosition, 190, yPosition)
                yPosition += 5

                // Add data rows
                doc.setFont("helvetica", "normal")
                leakTrends.forEach((trend: LeakTrendData) => {
                  if (yPosition > 270) {
                    // New page if needed
                    doc.addPage()
                    yPosition = 30
                  }

                  doc.text(trend.month || "", 25, yPosition)
                  doc.text(String(trend.activeLeaks ?? 0), 70, yPosition)
                  doc.text(String(trend.clearedLeaks ?? 0), 125, yPosition)
                  yPosition += 8
                })

                yPosition += 15
              }

              // End User Portal Devices Section
              if (deviceData.length > 0) {
                if (yPosition > 200) {
                  // New page if needed
                  doc.addPage()
                  yPosition = 30
                }

                doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
                doc.setFontSize(18)
                doc.setFont("helvetica", "bold")
                doc.text("End User Portal Devices", 20, yPosition)
                yPosition += 15

                doc.setFontSize(10)
                doc.setFont("helvetica", "normal")
                doc.setTextColor(0, 0, 0)

                deviceData.forEach((device: DevicePerformanceData, _index: number) => {
                  if (yPosition > 250) {
                    // New page if needed
                    doc.addPage()
                    yPosition = 30
                  }

                  // Customer name
                  doc.setFont("helvetica", "bold")
                  doc.text(`${device.customer || "Unknown"}:`, 25, yPosition)
                  yPosition += 8

                  // Device details
                  doc.setFont("helvetica", "normal")
                  const details = [
                    `Total Devices: ${device.totalDevices || 0}`,
                    `Active: ${device.activeDevices || 0}`,
                    `Alerts: ${device.alertDevices || 0}`,
                    `Offline: ${device.offlineDevices || 0}`,
                    `Efficiency: ${device.efficiency ? device.efficiency.toFixed(1) : 0}%`,
                  ]

                  details.forEach((detail, detailIndex) => {
                    doc.text(detail, 35, yPosition + detailIndex * 6)
                  })

                  yPosition += details.length * 6 + 8
                })
              }

              // Add footer
              const pageCount = doc.getNumberOfPages()
              for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i)
                doc.setFontSize(8)
                doc.setTextColor(128, 128, 128)
                doc.text(`Page ${i} of ${pageCount}`, 180, 285)
                doc.text("Water Bureau Admin Portal", 20, 285)
              }

              // Save the PDF
              doc.save(`${type}-analytics-export.pdf`)
              return
            }

            if (Array.isArray(data) && data.length > 0) {
              const councilsData = data as AdminCouncilInfo[]
              let yPosition = 60

              // Add summary section
              doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
              doc.setFontSize(16)
              doc.setFont("helvetica", "bold")
              doc.text("Customer Analytics Summary", 20, yPosition)
              yPosition += 15

              // Add summary stats
              const totalCouncils = councilsData.length
              const totalUsers = councilsData.reduce((sum, item) => sum + (item.userCount || 0), 0)
              const totalDevices = councilsData.reduce(
                (sum, item) => sum + (item.deviceCount || 0),
                0,
              )
              const totalAlerts = councilsData.reduce(
                (sum, item) => sum + (item.alertCount || 0),
                0,
              )

              doc.setFontSize(12)
              doc.setFont("helvetica", "normal")
              doc.setTextColor(0, 0, 0)

              const summaryData = [
                `Total Customers: ${totalCouncils}`,
                `Total End Users: ${totalUsers}`,
                `Total Devices: ${totalDevices}`,
                `Active Alerts: ${totalAlerts}`,
              ]

              summaryData.forEach((stat, index) => {
                doc.text(stat, 30, yPosition + index * 8)
              })

              yPosition += summaryData.length * 8 + 20

              // Process each customer
              councilsData.forEach((item, index) => {
                if (yPosition > 250) {
                  // New page if needed
                  doc.addPage()
                  yPosition = 30
                }

                // Customer header with background
                doc.setFillColor(240, 240, 240)
                doc.rect(15, yPosition - 5, 180, 15, "F")

                doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
                doc.setFontSize(14)
                doc.setFont("helvetica", "bold")
                doc.text(`${index + 1}. ${item.name || "Unnamed Customer"}`, 20, yPosition + 5)
                yPosition += 20

                // Customer details in a structured format
                doc.setFontSize(11)
                doc.setFont("helvetica", "normal")
                doc.setTextColor(0, 0, 0)

                const details = [
                  {
                    label: "Location:",
                    value: item.location || "N/A",
                    color: [0, 0, 0] as [number, number, number],
                  },
                  {
                    label: "End Users:",
                    value: item.userCount || 0,
                    color: [0, 0, 0] as [number, number, number],
                  },
                  {
                    label: "Devices:",
                    value: item.deviceCount || 0,
                    color: [0, 0, 0] as [number, number, number],
                  },
                  {
                    label: "Active:",
                    value: item.activeCount || 0,
                    color: [34, 139, 34] as [number, number, number],
                  },
                  {
                    label: "Offline:",
                    value: item.offlineCount || 0,
                    color: [169, 169, 169] as [number, number, number],
                  },
                  {
                    label: "Alerts:",
                    value: item.alertCount || 0,
                    color:
                      (item.alertCount ?? 0) > 0
                        ? accentColor
                        : ([0, 0, 0] as [number, number, number]),
                  },
                ]

                details.forEach((detail, detailIndex) => {
                  const xPos = detailIndex % 2 === 0 ? 25 : 110
                  const yPos = yPosition + Math.floor(detailIndex / 2) * 8

                  doc.setTextColor(detail.color[0], detail.color[1], detail.color[2])
                  doc.text(`${detail.label} ${detail.value}`, xPos, yPos)
                })

                yPosition += Math.ceil(details.length / 2) * 8 + 15

                // Add leak trends if available
                if (
                  item.leakTrends &&
                  Array.isArray(item.leakTrends) &&
                  item.leakTrends.length > 0
                ) {
                  doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
                  doc.setFontSize(12)
                  doc.setFont("helvetica", "bold")
                  doc.text("Leak Trends:", 20, yPosition)
                  yPosition += 10

                  doc.setFontSize(9)
                  doc.setFont("helvetica", "normal")
                  doc.setTextColor(0, 0, 0)

                  item.leakTrends
                    .slice(0, 3)
                    .forEach(
                      (trend: {
                        month?: string
                        leaks?: number
                        activeLeaks?: number
                        clearedLeaks?: number
                      }) => {
                        const trendText = `${trend.month || "N/A"}: ${trend.activeLeaks ?? 0} Active Leaks, ${trend.clearedLeaks ?? 0} Cleared Leaks`
                        doc.text(trendText, 30, yPosition)
                        yPosition += 6
                      },
                    )

                  yPosition += 10
                }
              })
            } else {
              doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
              doc.setFontSize(14)
              doc.text("No data available for export", 20, 70)
            }

            // Add footer
            const pageCount = doc.getNumberOfPages()
            for (let i = 1; i <= pageCount; i++) {
              doc.setPage(i)
              doc.setFontSize(8)
              doc.setTextColor(128, 128, 128)
              doc.text(`Page ${i} of ${pageCount}`, 180, 285)
              doc.text("Water Bureau Admin Portal", 20, 285)
            }

            // Save the PDF
            doc.save(`${type}-export.pdf`)
          })
          .catch((error) => {
            console.error("Error generating PDF:", error)
            // Fallback to simple PDF if jsPDF fails
            const fallbackContent = `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj

2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj

3 0 obj
<<
/Type /Page
/Parent 2 0 R
/MediaBox [0 0 612 792]
/Contents 4 0 R
>>
endobj

4 0 obj
<<
/Length 44
>>
stream
BT
/F1 12 Tf
100 700 Td
(${type} Export Data - Error generating PDF) Tj
ET
endstream
endobj

trailer
<<
/Size 5
/Root 1 0 R
>>
startxref
0
%%EOF`

            const blob = new Blob([fallbackContent], {
              type: "application/pdf",
            })
            const url = URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.download = `${type}-export.pdf`
            link.click()
            URL.revokeObjectURL(url)
          })
        return // Early return since PDF generation is async
      }

      case "png": {
        // Create a clean, structured PNG with analytics data
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")
        if (!ctx) {
          alert("PNG export not supported in this browser.")
          return
        }

        // Set canvas size
        canvas.width = 800
        canvas.height = 600

        // Background
        ctx.fillStyle = "#ffffff"
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // Header
        ctx.fillStyle = "#1e40af"
        ctx.fillRect(0, 0, canvas.width, 80)

        ctx.fillStyle = "#ffffff"
        ctx.font = "bold 28px Arial"
        ctx.fillText("Analytics Export Report", 30, 50)

        // Timestamp
        ctx.fillStyle = "#666666"
        ctx.font = "14px Arial"
        ctx.fillText(`Generated: ${new Date().toLocaleString()}`, 30, 110)

        if (type === "analytics" && typeof data === "object" && data !== null) {
          // Special handling for analytics PNG export
          const analyticsData = data as AnalyticsExportData
          const leakTrends = analyticsData.leakDetectionTrends || []
          const deviceData = analyticsData.endUserPortalDevices || []

          let yPosition = 140

          // Leak Analytics Section
          if (leakTrends.length > 0) {
            ctx.fillStyle = "#333333"
            ctx.font = "bold 18px Arial"
            ctx.fillText("Leak Analytics", 30, yPosition)
            yPosition += 25

            ctx.fillStyle = "#000000"
            ctx.font = "14px Arial"
            leakTrends.slice(0, 6).forEach((trend: LeakTrendData, _index: number) => {
              const yPos = yPosition + _index * 20
              ctx.fillText(
                `${trend.month || ""}: ${trend.activeLeaks ?? 0} Active Leaks, ${trend.clearedLeaks ?? 0} Cleared Leaks`,
                50,
                yPos,
              )
            })

            yPosition += leakTrends.slice(0, 6).length * 20 + 20
          }

          // Device Performance Section
          if (deviceData.length > 0 && yPosition < 500) {
            ctx.fillStyle = "#333333"
            ctx.font = "bold 18px Arial"
            ctx.fillText("Device Performance by Customer", 30, yPosition)
            yPosition += 25

            ctx.fillStyle = "#000000"
            ctx.font = "14px Arial"
            deviceData.slice(0, 4).forEach((device: DevicePerformanceData, _index: number) => {
              const yPos = yPosition + _index * 35
              ctx.fillText(`${device.customer || "Unknown"}:`, 50, yPos)
              ctx.fillText(
                `${device.totalDevices || 0} devices (${device.activeDevices || 0} active, ${device.alertDevices || 0} alerts)`,
                70,
                yPos + 18,
              )
            })
          }

          // Footer
          ctx.fillStyle = "#999999"
          ctx.font = "12px Arial"
          ctx.fillText("Water Bureau Admin Portal", 30, 580)

          // Convert to blob and download
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = url
                link.download = "analytics-export.png"
                link.click()
                URL.revokeObjectURL(url)
              }
            },
            "image/png",
            1.0,
          )

          return // Early return since PNG generation is async
        }

        if (Array.isArray(data) && data.length > 0) {
          const councilsData = data as AdminCouncilInfo[]
          // Summary section
          ctx.fillStyle = "#333333"
          ctx.font = "bold 18px Arial"
          ctx.fillText("Summary Statistics", 30, 150)

          const totalCouncils = councilsData.length
          const totalUsers = councilsData.reduce((sum, item) => sum + (item.userCount || 0), 0)
          const totalDevices = councilsData.reduce((sum, item) => sum + (item.deviceCount || 0), 0)
          const totalAlerts = councilsData.reduce((sum, item) => sum + (item.alertCount || 0), 0)

          ctx.fillStyle = "#000000"
          ctx.font = "16px Arial"
          const summary = [
            `Total Customers: ${totalCouncils}`,
            `Total End Users: ${totalUsers}`,
            `Total Devices: ${totalDevices}`,
            `Active Alerts: ${totalAlerts}`,
          ]

          summary.forEach((stat, index) => {
            ctx.fillText(stat, 50, 180 + index * 25)
          })

          // Top customers
          ctx.fillStyle = "#333333"
          ctx.font = "bold 18px Arial"
          ctx.fillText("Top Customers", 30, 300)

          ctx.fillStyle = "#000000"
          ctx.font = "14px Arial"
          councilsData.slice(0, 8).forEach((item, index) => {
            const yPos = 330 + index * 20
            const councilName = (item.name || "Unnamed Customer").substring(0, 25)
            const stats = `${item.userCount || 0} end users, ${item.deviceCount || 0} devices`
            ctx.fillText(`${index + 1}. ${councilName}`, 50, yPos)
            ctx.fillText(stats, 400, yPos)
          })

          // Simple bar chart for alerts
          ctx.fillStyle = "#333333"
          ctx.font = "bold 16px Arial"
          ctx.fillText("Alert Distribution", 30, 500)

          const councilsWithAlerts = councilsData
            .filter((item) => (item.alertCount || 0) > 0)
            .slice(0, 5)
          councilsWithAlerts.forEach((item, index) => {
            const barWidth = Math.min((item.alertCount || 0) * 20, 150)
            const xPos = 50
            const yPos = 520 + index * 25

            // Bar background
            ctx.fillStyle = "#e5e7eb"
            ctx.fillRect(xPos, yPos - 15, 150, 15)

            // Bar fill
            ctx.fillStyle = "#ef4444"
            ctx.fillRect(xPos, yPos - 15, barWidth, 15)

            // Label
            ctx.fillStyle = "#000000"
            ctx.font = "12px Arial"
            const label = (item.name || "Council").substring(0, 15)
            ctx.fillText(`${label}: ${item.alertCount}`, xPos + 160, yPos)
          })
        } else {
          ctx.fillStyle = "#666666"
          ctx.font = "16px Arial"
          ctx.fillText("No data available for export", 30, 200)
        }

        // Footer
        ctx.fillStyle = "#999999"
        ctx.font = "12px Arial"
        ctx.fillText("Water Bureau Admin Portal", 30, 580)

        // Convert to blob and download
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob)
              const link = document.createElement("a")
              link.href = url
              link.download = `${type}-analytics-export.png`
              link.click()
              URL.revokeObjectURL(url)
            }
          },
          "image/png",
          1.0,
        )

        return // Early return since PNG generation is async
      }

      default:
        content = JSON.stringify(data, null, 2)
        mimeType = "application/json;charset=utf-8;"
        filename = `${type}-export.json`
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
  }

  // If viewing a specific council dashboard (super admin accessing council)
  if (councilId && hasRole("super_admin")) {
    return <CouncilDashboard />
  }

  // Prevent council admin from accessing super admin dashboard via URL manipulation
  if (councilId && hasRole("council_admin")) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Access denied</p>
      </div>
    )
  }

  if (hasRole("super_admin")) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <p className="text-destructive">Error loading dashboard data</p>
        </div>
      )
    }

    const councilInfos = adminDashboardData?.councilInfos || []

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Manage dashboard analytics here</p>
        </div>

        <SuperAdminStatsSection councilInfos={councilInfos} />

        <AdvancedAnalyticsSection
          councilInfos={councilInfos}
          leakTrends={leakTrends}
          leakTrendsLoading={isLeakTrendsLoading}
          onExport={exportData}
        />

        <UserAnalyticsSection councilInfos={councilInfos} onExport={exportData} />
      </div>
    )
  }

  // Council Admin Dashboard - use the same rich dashboard as super admin council view
  if (user?.councils) {
    return <CouncilDashboard />
  }

  // Fallback for council admin without councilId
  return (
    <div className="flex items-center justify-center h-64">
      <p className="text-muted-foreground">No council assigned</p>
    </div>
  )
}
